import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Offer } from '../../../services/offerService';

interface OffersTableProps {
  offers: Offer[];
  onToggleStatus: (offer: Offer) => Promise<void>;
  onDelete: (offerId: string) => Promise<void>;
  onBulkActivate: (offerIds: string[]) => Promise<void>;
  onBulkDeactivate: (offerIds: string[]) => Promise<void>;
  onBulkDelete: (offerIds: string[]) => Promise<void>;
  loading?: boolean;
}

const OffersTable: React.FC<OffersTableProps> = ({ 
  offers, 
  onToggleStatus, 
  onDelete, 
  onBulkActivate,
  onBulkDeactivate,
  onBulkDelete,
  loading = false 
}) => {
  const navigate = useNavigate();
  const [processingOffers, setProcessingOffers] = useState<Set<string>>(new Set());
  const [selectedOffers, setSelectedOffers] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // Select/Deselect individual offers
  const handleSelectOffer = (offerId: string, checked: boolean) => {
    setSelectedOffers(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(offerId);
      } else {
        newSet.delete(offerId);
      }
      return newSet;
    });
  };

  // Select all / Deselect all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedOffers(new Set(offers.map(offer => offer.id)));
    } else {
      setSelectedOffers(new Set());
    }
  };

  // Bulk operations handlers
  const handleBulkActivate = async () => {
    if (selectedOffers.size === 0) return;
    
    setBulkLoading(true);
    try {
      await onBulkActivate(Array.from(selectedOffers));
      setSelectedOffers(new Set()); // Clear selection after successful operation
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDeactivate = async () => {
    if (selectedOffers.size === 0) return;
    
    setBulkLoading(true);
    try {
      await onBulkDeactivate(Array.from(selectedOffers));
      setSelectedOffers(new Set()); // Clear selection after successful operation
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedOffers.size === 0) return;
    
    if (window.confirm(`Are you sure you want to permanently delete ${selectedOffers.size} offer(s)? This action cannot be undone.`)) {
      setBulkLoading(true);
      try {
        await onBulkDelete(Array.from(selectedOffers));
        setSelectedOffers(new Set()); // Clear selection after successful operation
      } finally {
        setBulkLoading(false);
      }
    }
  };

  const handleToggleStatus = async (offer: Offer) => {
    setProcessingOffers(prev => new Set(prev).add(offer.id));
    try {
      await onToggleStatus(offer);
    } finally {
      setProcessingOffers(prev => {
        const newSet = new Set(prev);
        newSet.delete(offer.id);
        return newSet;
      });
    }
  };

  const handleDelete = async (offerId: string) => {
    if (window.confirm('Are you sure you want to delete this offer? This action cannot be undone.')) {
      setProcessingOffers(prev => new Set(prev).add(offerId));
      try {
        await onDelete(offerId);
      } finally {
        setProcessingOffers(prev => {
          const newSet = new Set(prev);
          newSet.delete(offerId);
          return newSet;
        });
      }
    }
  };

  const handleEdit = (offer: Offer) => {
    // Route to admin create offer screen with offer ID for editing
    window.location.href = `/admin-create-offer?edit=${offer.id}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'expired':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return 'CheckCircle';
      case 'inactive':
        return 'XCircle';
      case 'expired':
        return 'Clock';
      default:
        return 'AlertCircle';
    }
  };

  const allSelected = offers.length > 0 && selectedOffers.size === offers.length;
  const someSelected = selectedOffers.size > 0 && selectedOffers.size < offers.length;

  if (loading) {
    return (
      <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  if (offers.length === 0) {
    return (
      <div className="bg-surface rounded-lg border border-border p-6 shadow-sm">
        <div className="text-center py-8">
          <Icon name="Package" size={48} className="mx-auto mb-4 opacity-50 text-muted-foreground" />
          <h3 className="text-lg font-medium text-foreground mb-2">No offers found</h3>
          <p className="text-muted-foreground mb-4">Start by creating your first offer</p>
          <Button onClick={() => navigate('/create-offer')}>
            <Icon name="Plus" size={16} className="mr-2" />
            Create Offer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-border shadow-sm overflow-hidden" data-offers-table>
      <div className="px-6 py-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">All Offers</h3>
          <div className="text-sm text-muted-foreground">
            {offers.length} total offers
            {selectedOffers.size > 0 && ` • ${selectedOffers.size} selected`}
          </div>
        </div>
        
        {/* Bulk Actions Bar */}
        {selectedOffers.size > 0 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon name="CheckSquare" size={16} className="text-blue-600" />
                <span className="text-sm font-medium text-blue-900">
                  {selectedOffers.size} offer(s) selected
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkActivate}
                  disabled={bulkLoading}
                  className="text-green-700 border-green-300 hover:bg-green-50"
                >
                  {bulkLoading ? (
                    <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                  ) : (
                    <Icon name="PlayCircle" size={16} className="mr-2" />
                  )}
                  Activate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkDeactivate}
                  disabled={bulkLoading}
                  className="text-orange-700 border-orange-300 hover:bg-orange-50"
                >
                  {bulkLoading ? (
                    <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                  ) : (
                    <Icon name="PauseCircle" size={16} className="mr-2" />
                  )}
                  Deactivate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={bulkLoading}
                  className="text-red-700 border-red-300 hover:bg-red-50"
                >
                  {bulkLoading ? (
                    <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                  ) : (
                    <Icon name="Trash2" size={16} className="mr-2" />
                  )}
                  Delete
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedOffers(new Set())}
                  className="text-gray-600"
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30">
            <tr>
              <th className="px-6 py-3 text-left">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(checked) => handleSelectAll(checked)}
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Offer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Discount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {offers.map((offer) => (
              <tr key={offer.id} className="hover:bg-muted/20">
                <td className="px-6 py-4">
                  <Checkbox
                    checked={selectedOffers.has(offer.id)}
                    onChange={(checked) => handleSelectOffer(offer.id, checked)}
                  />
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-12 w-12">
                      {offer.imageUrl ? (
                        <img
                          className="h-12 w-12 rounded-lg object-cover"
                          src={offer.imageUrl}
                          alt={offer.title}
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
                          <Icon name="Image" size={20} className="text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-foreground line-clamp-1">
                        {offer.title}
                      </div>
                      <div className="text-sm text-muted-foreground line-clamp-1">
                        {offer.address || 'No address specified'}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-foreground">{offer.category}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(offer.status)}`}>
                    <Icon name={getStatusIcon(offer.status)} size={12} className="mr-1" />
                    {offer.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                  {offer.discountType === 'percentage' ? (
                    `${offer.discountPercentage}% off`
                  ) : offer.discountType === 'amount' ? (
                    `€${offer.discountAmount} off`
                  ) : offer.discountType === 'one_plus_one' ? (
                    '1+1 Offer'
                  ) : (
                    'Free Goodie'
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                  {new Date(offer.createdAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleStatus(offer)}
                      disabled={processingOffers.has(offer.id)}
                      className="p-2"
                      title={offer.status === 'active' ? 'Deactivate offer' : 'Activate offer'}
                    >
                      {processingOffers.has(offer.id) ? (
                        <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
                      ) : (
                        <Icon 
                          name={offer.status === 'active' ? 'PauseCircle' : 'PlayCircle'} 
                          size={16}
                          className={offer.status === 'active' ? 'text-orange-600' : 'text-green-600'}
                        />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(offer)}
                      className="p-2"
                      title="Edit offer"
                    >
                      <Icon name="Edit" size={16} className="text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(offer.id)}
                      disabled={processingOffers.has(offer.id)}
                      className="p-2"
                      title="Delete offer"
                    >
                      {processingOffers.has(offer.id) ? (
                        <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
                      ) : (
                        <Icon name="Trash2" size={16} className="text-red-600" />
                      )}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-border">
        {offers.map((offer) => (
          <div key={offer.id} className="p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 pt-1">
                <Checkbox
                  checked={selectedOffers.has(offer.id)}
                  onChange={(checked) => handleSelectOffer(offer.id, checked)}
                />
              </div>
              <div className="flex-shrink-0">
                {offer.imageUrl ? (
                  <img
                    className="h-16 w-16 rounded-lg object-cover"
                    src={offer.imageUrl}
                    alt={offer.title}
                  />
                ) : (
                  <div className="h-16 w-16 rounded-lg bg-muted flex items-center justify-center">
                    <Icon name="Image" size={24} className="text-muted-foreground" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-foreground mb-1 line-clamp-2">
                      {offer.title}
                    </h4>
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(offer.status)}`}>
                        <Icon name={getStatusIcon(offer.status)} size={10} className="mr-1" />
                        {offer.status}
                      </span>
                      <span className="text-xs text-muted-foreground">{offer.category}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mb-2">
                      {offer.discountType === 'percentage' ? (
                        `${offer.discountPercentage}% off`
                      ) : offer.discountType === 'amount' ? (
                        `€${offer.discountAmount} off`
                      ) : offer.discountType === 'one_plus_one' ? (
                        '1+1 Offer'
                      ) : (
                        'Free Goodie'
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Created {new Date(offer.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-end space-x-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleStatus(offer)}
                    disabled={processingOffers.has(offer.id)}
                    className="text-xs"
                  >
                    {processingOffers.has(offer.id) ? (
                      <div className="animate-spin w-3 h-3 border border-current border-t-transparent rounded-full mr-1" />
                    ) : (
                      <Icon 
                        name={offer.status === 'active' ? 'PauseCircle' : 'PlayCircle'} 
                        size={12} 
                        className="mr-1" 
                      />
                    )}
                    {offer.status === 'active' ? 'Pause' : 'Activate'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(offer)}
                    className="text-xs"
                  >
                    <Icon name="Edit" size={12} className="mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(offer.id)}
                    disabled={processingOffers.has(offer.id)}
                    className="text-xs text-red-600 border-red-200 hover:bg-red-50"
                  >
                    {processingOffers.has(offer.id) ? (
                      <div className="animate-spin w-3 h-3 border border-current border-t-transparent rounded-full mr-1" />
                    ) : (
                      <Icon name="Trash2" size={12} className="mr-1" />
                    )}
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OffersTable;